import {
    createSlice, createAsyncThunk, PayloadAction, type Dispatch
} from '@reduxjs/toolkit';
import type {TemplateSuggestion, TemplateSuggestionsResponse, FlushTemplateCacheResponse} from './api';
import {fetchTemplateSuggestions as fetchSuggestionsApi, flushTemplateCache as flushCacheApi} from './api';

interface AIState {
    suggestions: TemplateSuggestion[];
    isSuggestionsLoading: boolean;
    error: string | null | undefined;
    flushResult: FlushTemplateCacheResponse | null;
}

export type AppDispatch = Dispatch;

const initialState: AIState = {
    suggestions: [],
    isSuggestionsLoading: false,
    error: null,
    flushResult: null
};

export const fetchTemplateSuggestions = createAsyncThunk(
    'ai/fetchTemplateSuggestions',
    async ({
        searchQuery,
        includeShared = true
    }: {
        searchQuery: string;
        includeShared?: boolean
    }) => fetchSuggestionsApi(searchQuery, includeShared)
);

export const flushTemplateCache = createAsyncThunk(
    'ai/flushTemplateCache',
    ({templateId}: {templateId?: string}) => flushCacheApi(templateId)
);

const aiSlice = createSlice({
    name: 'ai',
    initialState,
    reducers: {
        clearSuggestions: (state) => {
            state.suggestions = [];
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchTemplateSuggestions.pending, (state) => {
                state.isSuggestionsLoading = true;
                state.error = null;
            })
            .addCase(fetchTemplateSuggestions.fulfilled, (state, action: PayloadAction<TemplateSuggestionsResponse>) => {
                state.isSuggestionsLoading = false;
                state.suggestions = action.payload.suggestions;
                state.error = null;
            })
            .addCase(fetchTemplateSuggestions.rejected, (state, action) => {
                state.isSuggestionsLoading = false;
                state.error = action.error.message || 'ai.error.unknown';
            })
            .addCase(flushTemplateCache.pending, (state) => {
                state.error = null;
            })
            .addCase(flushTemplateCache.fulfilled, (state, action: PayloadAction<FlushTemplateCacheResponse>) => {
                state.flushResult = action.payload;
                state.error = null;
            })
            .addCase(flushTemplateCache.rejected, (state, action) => {
                state.error = action.error.message || 'ai.error.unknown';
            });
    }
});

export const {clearSuggestions} = aiSlice.actions;
export const selectIsSuggestionsLoading = (state: {ai: AIState}) => state.ai.isSuggestionsLoading;
export const selectSuggestions = (state: {ai: AIState}) => state.ai.suggestions;
export const selectError = (state: {ai: AIState}) => state.ai.error;

export type RootState = { ai: AIState };
export type {AIState};

export default aiSlice.reducer;
